/**
 * Helpers for admin scenario planning: thresholds and conversion rates serialized as API query
 * parameters (see backend `get_raw_scenario_query`).
 */

const SCENARIO_SESSION_KEY_PREFIX = 'advancewell:analytics-scenario:'

/** Full set of scenario fields appended to analytics `GET` requests when a scenario is applied. */
export type AnalyticsScenarioParams = {
    threshold_very_engaged: number
    threshold_engaged: number
    threshold_somewhat_engaged: number
    threshold_minimally_engaged: number
    rate_very_engaged: number
    rate_engaged: number
    rate_somewhat_engaged: number
    rate_minimally_engaged: number
    rate_disengaged: number
}

const STORED_SCENARIO_KEYS: (keyof AnalyticsScenarioParams)[] = [
    'threshold_very_engaged',
    'threshold_engaged',
    'threshold_somewhat_engaged',
    'threshold_minimally_engaged',
    'rate_very_engaged',
    'rate_engaged',
    'rate_somewhat_engaged',
    'rate_minimally_engaged',
    'rate_disengaged',
]

/** Minimum score cutoffs per engagement band matching product defaults for MLR or RFM basis. */
export function defaultScenarioThresholds(
    useRfm: boolean,
): Pick<
    AnalyticsScenarioParams,
    | 'threshold_very_engaged'
    | 'threshold_engaged'
    | 'threshold_somewhat_engaged'
    | 'threshold_minimally_engaged'
> {
    if (useRfm) {
        return {
            threshold_very_engaged: 251,
            threshold_engaged: 226,
            threshold_somewhat_engaged: 201,
            threshold_minimally_engaged: 1,
        }
    }
    return {
        threshold_very_engaged: 80,
        threshold_engaged: 60,
        threshold_somewhat_engaged: 40,
        threshold_minimally_engaged: 1,
    }
}

/** Default per-level conversion fractions (0–1) used in yield and aggregate capacity math. */
export function defaultScenarioRates(): Pick<
    AnalyticsScenarioParams,
    | 'rate_very_engaged'
    | 'rate_engaged'
    | 'rate_somewhat_engaged'
    | 'rate_minimally_engaged'
    | 'rate_disengaged'
> {
    return {
        rate_very_engaged: 0.15,
        rate_engaged: 0.1,
        rate_somewhat_engaged: 0.05,
        rate_minimally_engaged: 0.01,
        rate_disengaged: 0,
    }
}

/** Thresholds and rates combined for the given engagement basis (MLR vs RFM cutoffs). */
export function fullDefaultScenario(useRfm: boolean): AnalyticsScenarioParams {
    return {...defaultScenarioThresholds(useRfm), ...defaultScenarioRates()}
}

/** `sessionStorage` key for persisting a scenario for one organization in this browser tab. */
export function scenarioSessionStorageKey(orgId: string): string {
    return `${SCENARIO_SESSION_KEY_PREFIX}${orgId}`
}

/** JSON used as `sessionStorage` value for {@link scenarioSessionStorageKey}. */
export function serializeAnalyticsScenario(params: AnalyticsScenarioParams): string {
    return JSON.stringify(params)
}

/** Parses stored JSON; returns null if invalid or incomplete. */
export function parseStoredAnalyticsScenario(raw: string): AnalyticsScenarioParams | null {
    try {
        const data = JSON.parse(raw) as unknown
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            return null
        }
        const record = data as Record<string, unknown>
        for (const key of STORED_SCENARIO_KEYS) {
            const value = record[key]
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return null
            }
        }
        return {
            threshold_very_engaged: record['threshold_very_engaged'] as number,
            threshold_engaged: record['threshold_engaged'] as number,
            threshold_somewhat_engaged: record['threshold_somewhat_engaged'] as number,
            threshold_minimally_engaged: record['threshold_minimally_engaged'] as number,
            rate_very_engaged: record['rate_very_engaged'] as number,
            rate_engaged: record['rate_engaged'] as number,
            rate_somewhat_engaged: record['rate_somewhat_engaged'] as number,
            rate_minimally_engaged: record['rate_minimally_engaged'] as number,
            rate_disengaged: record['rate_disengaged'] as number,
        }
    } catch {
        return null
    }
}

/** Append all nine scenario keys to `params` (snake_case names expected by the API). */
export function appendAnalyticsScenarioParams(
    params: URLSearchParams,
    scenario: AnalyticsScenarioParams,
): void {
    params.set('threshold_very_engaged', String(scenario.threshold_very_engaged))
    params.set('threshold_engaged', String(scenario.threshold_engaged))
    params.set('threshold_somewhat_engaged', String(scenario.threshold_somewhat_engaged))
    params.set('threshold_minimally_engaged', String(scenario.threshold_minimally_engaged))
    params.set('rate_very_engaged', String(scenario.rate_very_engaged))
    params.set('rate_engaged', String(scenario.rate_engaged))
    params.set('rate_somewhat_engaged', String(scenario.rate_somewhat_engaged))
    params.set('rate_minimally_engaged', String(scenario.rate_minimally_engaged))
    params.set('rate_disengaged', String(scenario.rate_disengaged))
}
